import { useState } from 'react';
import { Badge, Group } from '@mantine/core';
import { IconGaugeOff } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { LocationFilterSelect, ProductFilterSelect, SiteFilterSelect } from '../components/FilterSelects';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { rangeQuery, useSiloDriftReport } from '../api';
import { formatDateTime, formatKg, formatPct } from '../format';
import type { SiloDriftRow } from '../types';

function varianceColor(row: SiloDriftRow): string {
  const pct = row.variancePct !== null ? Math.abs(Number(row.variancePct)) : null;
  if (pct === null) return 'graphite';
  if (pct >= 15) return 'red';
  if (pct >= 5) return 'amber';
  return 'green';
}

/** Report #5 (BUILD_SPEC.md §13) — silo drift (estimated vs ledger mass) over
 * time, per silo. */
export function SiloDriftReport() {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const query = useSiloDriftReport({ siteId: siteId ?? undefined, locationId: locationId ?? undefined, productId: productId ?? undefined, from, to });
  const rows = query.data?.items ?? [];

  const columns: ReportColumn<SiloDriftRow>[] = [
    { key: 'readAt', header: 'Read at', render: (r) => formatDateTime(r.readAt) },
    { key: 'location', header: 'Silo', render: (r) => `${r.locationCode} — ${r.locationName}` },
    { key: 'product', header: 'Product', render: (r) => r.productSku },
    { key: 'level', header: 'Level', align: 'right', render: (r) => `${r.levelValue}${r.levelUnit === 'PCT' ? '%' : 'm'}` },
    { key: 'estMassKg', header: 'Estimated', align: 'right', render: (r) => formatKg(r.estMassKg) },
    { key: 'ledgerQtyKg', header: 'Ledger', align: 'right', render: (r) => formatKg(r.ledgerQtyKg) },
    {
      key: 'variance',
      header: 'Variance',
      align: 'right',
      render: (r) => (
        <Badge color={varianceColor(r)} variant="light">
          {formatKg(r.varianceKg)} ({formatPct(r.variancePct)})
        </Badge>
      ),
    },
    { key: 'status', header: 'Status', render: (r) => r.status },
  ];

  return (
    <ReportPage
      title="Silo drift history"
      description="Estimated (from a silo level reading + calibration) vs ledger mass, over time."
      action={
        <CsvDownloadButton
          path="/reporting/silo-drift"
          query={{
            siteId: siteId ?? undefined,
            locationId: locationId ?? undefined,
            productId: productId ?? undefined,
            ...rangeQuery(from, to),
          }}
          filename="silo-drift.csv"
          description="Exports the silo drift history, matching your filters, as a CSV file"
        />
      }
    >
      <Group gap="md" wrap="wrap" align="flex-end">
        <SiteFilterSelect value={siteId} onChange={setSiteId} label="Site" w={200} />
        <LocationFilterSelect value={locationId} onChange={setLocationId} siteId={siteId ?? undefined} label="Silo" w={200} />
        <ProductFilterSelect value={productId} onChange={setProductId} label="Product" w={220} />
        <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      </Group>

      <ReportTable
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.id}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage="Failed to load silo drift history."
        emptyMessage="No silo readings for the selected filters"
        emptyHint="Try a different silo, product or date range."
        emptyIcon={IconGaugeOff}
      />
    </ReportPage>
  );
}
